import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle, Clock, Save, Send, XCircle } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { usePublicSurvey, useSaveDraft, useSubmitPublicSurvey } from '@/features/surveys/api/use-public-survey';
import type { PublicQuestionOutput, PublicSectionOutput } from '@/features/surveys/types';

export const Route = createFileRoute('/survey-respond/$token')({
  component: PublicSurveyRespondPage,
});

function SurveyLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="space-y-2 text-center">
            <Skeleton className="mx-auto h-8 w-64" />
            <Skeleton className="mx-auto h-4 w-40" />
          </div>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  message: string;
  icon: React.ReactNode;
}

function ErrorState({ title, message, icon }: ErrorStateProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{message}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function ThankYouState() {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background p-4"
      data-testid="thank-you-message"
    >
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle>Thank You!</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Your survey response has been submitted successfully. You may close this window.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

interface QuestionFieldProps {
  question: PublicQuestionOutput;
  value: Record<string, unknown> | undefined;
  onChange: (questionId: string, value: Record<string, unknown>) => void;
}

function QuestionField({ question, value, onChange }: QuestionFieldProps) {
  const currentValue = value?.value as string | undefined;

  const handleChange = useCallback(
    (val: string) => {
      onChange(question.id, { value: val });
    },
    [question.id, onChange],
  );

  const handleMultiChange = useCallback(
    (optionValue: string, checked: boolean) => {
      const current = (value?.value ?? []) as string[];
      const next = checked
        ? [...current, optionValue]
        : current.filter((v) => v !== optionValue);
      onChange(question.id, { value: next });
    },
    [question.id, value, onChange],
  );

  switch (question.questionType) {
    case 'text':
    case 'email':
      return (
        <Input
          data-testid={`question-input-${question.id}`}
          type={question.questionType === 'email' ? 'email' : 'text'}
          value={currentValue ?? ''}
          onChange={(e) => { handleChange(e.target.value); }}
          placeholder={question.helpText ?? ''}
        />
      );

    case 'textarea':
      return (
        <Textarea
          data-testid={`question-input-${question.id}`}
          value={currentValue ?? ''}
          onChange={(e) => { handleChange(e.target.value); }}
          placeholder={question.helpText ?? ''}
          rows={4}
        />
      );

    case 'number':
    case 'currency':
    case 'percentage':
      return (
        <Input
          data-testid={`question-input-${question.id}`}
          type="number"
          value={currentValue ?? ''}
          onChange={(e) => { handleChange(e.target.value); }}
          placeholder={question.helpText ?? ''}
        />
      );

    case 'date':
      return (
        <Input
          data-testid={`question-input-${question.id}`}
          type="date"
          value={currentValue ?? ''}
          onChange={(e) => { handleChange(e.target.value); }}
        />
      );

    case 'single_choice':
      return (
        <RadioGroup
          data-testid={`question-input-${question.id}`}
          value={currentValue ?? ''}
          onValueChange={handleChange}
        >
          {question.options?.map((opt) => (
            <div key={opt.value} className="flex items-center space-x-2">
              <RadioGroupItem value={opt.value} id={`${question.id}-${opt.value}`} />
              <Label htmlFor={`${question.id}-${opt.value}`}>{opt.label}</Label>
            </div>
          ))}
        </RadioGroup>
      );

    case 'multi_choice':
      return (
        <div data-testid={`question-input-${question.id}`} className="space-y-2">
          {question.options?.map((opt) => {
            const checked = ((value?.value ?? []) as string[]).includes(opt.value);
            return (
              <div key={opt.value} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`${question.id}-${opt.value}`}
                  checked={checked}
                  onChange={(e) => { handleMultiChange(opt.value, e.target.checked); }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor={`${question.id}-${opt.value}`}>{opt.label}</Label>
              </div>
            );
          })}
        </div>
      );

    case 'yes_no':
      return (
        <RadioGroup
          data-testid={`question-input-${question.id}`}
          value={currentValue ?? ''}
          onValueChange={handleChange}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${question.id}-yes`} />
            <Label htmlFor={`${question.id}-yes`}>Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${question.id}-no`} />
            <Label htmlFor={`${question.id}-no`}>No</Label>
          </div>
        </RadioGroup>
      );

    case 'rating_scale':
    case 'likert':
      return (
        <RadioGroup
          data-testid={`question-input-${question.id}`}
          value={currentValue ?? ''}
          onValueChange={handleChange}
          className="flex space-x-2"
        >
          {(question.options ?? [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }, { value: '5', label: '5' }]).map(
            (opt) => (
              <div key={opt.value} className="flex flex-col items-center space-y-1">
                <RadioGroupItem value={opt.value} id={`${question.id}-${opt.value}`} />
                <Label htmlFor={`${question.id}-${opt.value}`} className="text-xs">
                  {opt.label}
                </Label>
              </div>
            ),
          )}
        </RadioGroup>
      );

    default:
      return (
        <Input
          data-testid={`question-input-${question.id}`}
          value={currentValue ?? ''}
          onChange={(e) => { handleChange(e.target.value); }}
          placeholder={question.helpText ?? ''}
        />
      );
  }
}

function SurveyForm({
  sections,
  existingAnswers,
  token,
  campaignName,
  templateName,
  customMessage,
  deadline,
  recipientName,
}: {
  sections: PublicSectionOutput[];
  existingAnswers: { questionId: string; responseValue: Record<string, unknown> | null }[] | null;
  token: string;
  campaignName: string;
  templateName: string;
  customMessage: string | null;
  deadline: string | null;
  recipientName: string | null;
}) {
  const [answers, setAnswers] = useState<Record<string, Record<string, unknown>>>(() => {
    const initial: Record<string, Record<string, unknown>> = {};
    if (existingAnswers) {
      for (const a of existingAnswers) {
        if (a.responseValue) {
          initial[a.questionId] = a.responseValue;
        }
      }
    }
    return initial;
  });
  const [submitted, setSubmitted] = useState(false);

  const submitMutation = useSubmitPublicSurvey(token);
  const draftMutation = useSaveDraft(token);

  const allQuestions = useMemo(
    () => sections.flatMap((s) => s.questions),
    [sections],
  );

  const requiredQuestions = useMemo(
    () => allQuestions.filter((q) => q.isRequired),
    [allQuestions],
  );

  const answeredCount = useMemo(
    () => allQuestions.filter((q) => answers[q.id]?.value).length,
    [allQuestions, answers],
  );

  const progress = allQuestions.length > 0 ? Math.round((answeredCount / allQuestions.length) * 100) : 0;

  const canSubmit = useMemo(
    () => requiredQuestions.every((q) => answers[q.id]?.value),
    [requiredQuestions, answers],
  );

  const handleChange = useCallback((questionId: string, value: Record<string, unknown>) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleSaveDraft = useCallback(async () => {
    const answerList = Object.entries(answers).map(([questionId, responseValue]) => ({
      questionId,
      responseValue,
    }));
    await draftMutation.mutateAsync({ answers: answerList });
  }, [answers, draftMutation]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    const answerList = Object.entries(answers).map(([questionId, responseValue]) => ({
      questionId,
      responseValue,
    }));
    try {
      await submitMutation.mutateAsync({ answers: answerList });
      setSubmitted(true);
    } catch {
      // Error state is handled by submitMutation.isError below
    }
  }, [answers, canSubmit, submitMutation]);

  if (submitted) {
    return <ThankYouState />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-6" data-testid="survey-form-container">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-bold">{campaignName}</h1>
            <p className="mt-1 text-muted-foreground">{templateName}</p>
            {recipientName && (
              <p className="mt-1 text-sm text-muted-foreground">Welcome, {recipientName}</p>
            )}
          </div>

          {/* Custom message */}
          {customMessage && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">{customMessage}</p>
              </CardContent>
            </Card>
          )}

          {/* Deadline */}
          {deadline && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Deadline: {new Date(deadline).toLocaleDateString()}</span>
            </div>
          )}

          {/* Progress */}
          <div data-testid="progress-bar">
            <div className="mb-1 flex justify-between text-sm text-muted-foreground">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} />
          </div>

          {/* Sections */}
          {sections.map((section) => (
            <Card key={section.id}>
              <CardHeader>
                <CardTitle className="text-lg">{section.name}</CardTitle>
                {section.description && (
                  <CardDescription>{section.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {section.questions.map((question) => (
                  <div key={question.id} className="space-y-2">
                    <Label className="text-sm font-medium">
                      {question.text}
                      {question.isRequired && (
                        <span className="ml-1 text-destructive">*</span>
                      )}
                    </Label>
                    {question.helpText && (
                      <p className="text-xs text-muted-foreground">{question.helpText}</p>
                    )}
                    <QuestionField
                      question={question}
                      value={answers[question.id]}
                      onChange={handleChange}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {/* Action buttons */}
          <div className="flex justify-between gap-4">
            <Button
              variant="outline"
              onClick={() => void handleSaveDraft()}
              disabled={draftMutation.isPending}
              data-testid="save-draft-button"
            >
              <Save className="mr-2 h-4 w-4" />
              {draftMutation.isPending ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={!canSubmit || submitMutation.isPending}
              data-testid="submit-button"
            >
              <Send className="mr-2 h-4 w-4" />
              {submitMutation.isPending ? 'Submitting...' : 'Submit Survey'}
            </Button>
          </div>

          {submitMutation.isError && (
            <p className="text-center text-sm text-destructive">
              Failed to submit. Please try again.
            </p>
          )}
          {draftMutation.isSuccess && (
            <p className="text-center text-sm text-green-600">Draft saved successfully.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PublicSurveyRespondPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError } = usePublicSurvey(token);

  if (isLoading) {
    return <SurveyLoadingSkeleton />;
  }

  if (isError || !data) {
    return (
      <ErrorState
        title="Invalid Link"
        message="This survey link is invalid or has expired. Please check the link and try again."
        icon={<AlertCircle className="h-8 w-8 text-muted-foreground" />}
      />
    );
  }

  if (data.status === 'submitted') {
    return (
      <ErrorState
        title="Already Submitted"
        message="You have already submitted your response to this survey. Thank you!"
        icon={<CheckCircle className="h-8 w-8 text-green-600" />}
      />
    );
  }

  if (data.isClosed) {
    return (
      <ErrorState
        title="Survey Closed"
        message="This survey is no longer accepting responses."
        icon={<XCircle className="h-8 w-8 text-muted-foreground" />}
      />
    );
  }

  return (
    <SurveyForm
      sections={data.sections}
      existingAnswers={data.existingAnswers}
      token={token}
      campaignName={data.campaignName}
      templateName={data.templateName}
      customMessage={data.customMessage}
      deadline={data.deadline}
      recipientName={data.recipientName}
    />
  );
}
